"use client";

import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/modal";
import { Search } from "lucide-react";

import { HighlightedText } from "@/components/docs/HighlightedText";
import {
  type DocsIndexDocument,
  type DocsSearchHit,
  useDocsSearchModal,
} from "@/components/docs/useDocsSearchModal";
import type { DocsSearchScope } from "@/lib/docs-search-shared";

function snippet(text: string, query: string, maxLen = 120): string {
  const lower = text.toLowerCase();
  const q = query.trim().toLowerCase();
  const words = q.split(/\s+/).filter((w) => w.length > 1);
  let idx = 0;

  for (const w of words) {
    const i = lower.indexOf(w);

    if (i >= 0) {
      idx = Math.max(0, i - 24);
      break;
    }
  }

  const slice = text.slice(idx, idx + maxLen).trim();
  const ell = idx > 0 ? "…" : "";

  return `${ell}${slice}${text.length > idx + maxLen ? "…" : ""}`;
}

function DocsSearchScopeChips({
  scope,
  setScope,
}: {
  scope: DocsSearchScope;
  setScope: (s: DocsSearchScope) => void;
}) {
  const options: { key: DocsSearchScope; label: string }[] = [
    { key: "all", label: "All" },
    { key: "guide", label: "Guides" },
    { key: "faq", label: "FAQ" },
    { key: "screens", label: "Screens" },
  ];

  return (
    <div
      aria-label="Search scope"
      className="flex flex-wrap gap-2"
      role="group"
    >
      {options.map(({ key, label }) => (
        <Button
          key={key}
          size="sm"
          variant={scope === key ? "solid" : "bordered"}
          onPress={() => setScope(key)}
        >
          {label}
        </Button>
      ))}
    </div>
  );
}

function DocsSearchResultRow({
  doc,
  goTo,
  href,
  isSelected,
  onHover,
  query,
  title,
}: {
  doc: DocsIndexDocument | undefined;
  goTo: (href: string) => void;
  href: string;
  isSelected: boolean;
  onHover: () => void;
  query: string;
  title: string;
}) {
  const subSource =
    doc != null ? doc.description || snippet(doc.text, query) : "";
  const sub = subSource ? snippet(subSource, query) : "";

  return (
    <li>
      <button
        className={`w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-content1 ${
          isSelected
            ? "bg-primary/15 text-default-900"
            : "text-default-700 hover:bg-default-100"
        }`}
        type="button"
        onClick={() => goTo(href)}
        onMouseEnter={onHover}
      >
        <span className="font-medium text-default-900">
          <HighlightedText query={query} text={title} />
        </span>
        {sub ? (
          <span className="mt-0.5 block line-clamp-2 text-xs text-default-500">
            <HighlightedText query={query} text={sub} />
          </span>
        ) : null}
        <span className="mt-1 block font-mono text-[11px] text-default-400">
          {href}
        </span>
      </button>
    </li>
  );
}

function DocsSearchResults({
  documents,
  goTo,
  query,
  results,
  selectedIndex,
  setSelectedIndex,
}: {
  documents: DocsIndexDocument[];
  goTo: (href: string) => void;
  query: string;
  results: DocsSearchHit[];
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
}) {
  return (
    <ul className="flex flex-col">
      {results.map((r, i) => (
        <DocsSearchResultRow
          key={r.id}
          doc={documents.find((d) => d.id === r.id)}
          goTo={goTo}
          href={r.href}
          isSelected={i === selectedIndex}
          query={query}
          title={r.title}
          onHover={() => setSelectedIndex(i)}
        />
      ))}
    </ul>
  );
}

function DocsSearchRecentList({
  docs,
  goTo,
}: {
  docs: DocsIndexDocument[];
  goTo: (href: string) => void;
}) {
  if (docs.length === 0) return null;

  return (
    <div className="mt-4">
      <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-default-500">
        Recently updated
      </p>
      <ul className="flex flex-col gap-0.5">
        {docs.map((d) => (
          <li key={d.id}>
            <button
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-default-700 transition-colors hover:bg-default-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-content1"
              type="button"
              onClick={() => goTo(d.href)}
            >
              <span className="font-medium text-default-900">{d.title}</span>
              <span className="mt-0.5 block font-mono text-[11px] text-default-400">
                {d.href}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocsSearchSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-3 py-4">
      {[1, 2, 3, 4, 5].map((k) => (
        <div key={k} className="animate-pulse space-y-2 rounded-lg px-3 py-2">
          <div className="h-4 max-w-[12rem] rounded bg-default-200" />
          <div className="h-3 w-full rounded bg-default-100" />
        </div>
      ))}
    </div>
  );
}

function DocsSearchHint({ loadError }: { loadError: string | null }) {
  if (loadError === null) return null;

  return <p className="text-xs text-default-500">{loadError}</p>;
}

/* eslint-disable complexity -- branchy UI state machine for modal body */
function DocsSearchModalBody({
  canSearch,
  documents,
  goTo,
  indexLoading,
  loadError,
  query,
  recentDocs,
  results,
  selectedIndex,
  setSelectedIndex,
}: {
  canSearch: boolean;
  documents: DocsIndexDocument[];
  goTo: (href: string) => void;
  indexLoading: boolean;
  loadError: string | null;
  query: string;
  recentDocs: DocsIndexDocument[];
  results: DocsSearchHit[];
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
}) {
  const q = query.trim();

  if (indexLoading && loadError === null) {
    return <DocsSearchSkeleton />;
  }

  if (!canSearch && !loadError) {
    return <DocsSearchSkeleton />;
  }

  if (loadError) {
    return null;
  }

  if (canSearch && q === "") {
    return (
      <div>
        <p className="py-2 text-center text-sm text-default-500">
          Type to search all guides, FAQ, and screen articles. Filter with the
          chips above.
        </p>
        <DocsSearchRecentList docs={recentDocs} goTo={goTo} />
      </div>
    );
  }

  if (canSearch && q !== "" && results.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-default-500">
        No results for &ldquo;{q}&rdquo;.
      </p>
    );
  }

  return (
    <DocsSearchResults
      documents={documents}
      goTo={goTo}
      query={query}
      results={results}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
    />
  );
}
/* eslint-enable complexity */

function DocsSearchLiveRegion({ message }: { message: string }) {
  return (
    <p aria-atomic="true" aria-live="polite" className="sr-only">
      {message}
    </p>
  );
}

/* eslint-disable complexity -- dialog rendering has explicit UI branches */
function DocsSearchDialog(props: ReturnType<typeof useDocsSearchModal>) {
  const {
    canSearch,
    documents,
    goTo,
    indexLoading,
    loadError,
    onModalKeyDown,
    open,
    query,
    recentDocs,
    results,
    scope,
    selectedIndex,
    setOpen,
    setQuery,
    setScope,
    setSelectedIndex,
  } = props;

  const q = query.trim();
  const liveMsg =
    canSearch && q.length > 0
      ? `${results.length} result${results.length === 1 ? "" : "s"}`
      : "";

  return (
    <Modal
      isDismissable
      classNames={{
        base: "m-0 h-[100dvh] max-h-[100dvh] rounded-none sm:m-4 sm:h-auto sm:max-h-[min(90vh,720px)] sm:rounded-2xl",
        body: "overflow-y-auto",
      }}
      isOpen={open}
      scrollBehavior="inside"
      size="2xl"
      onOpenChange={setOpen}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-3 border-b border-default-200 pb-4">
          <span>Search documentation</span>
          <Input
            aria-label="Search documentation"
            autoFocus={open}
            isDisabled={!canSearch}
            placeholder={
              canSearch || indexLoading
                ? "Search titles, descriptions, and article text…"
                : "Index unavailable"
            }
            size="lg"
            startContent={<Search className="h-4 w-4 text-default-400" />}
            value={query}
            variant="bordered"
            onKeyDown={onModalKeyDown}
            onValueChange={setQuery}
          />
          {canSearch ? (
            <DocsSearchScopeChips scope={scope} setScope={setScope} />
          ) : null}
        </ModalHeader>
        <ModalBody className="gap-0 py-2">
          <DocsSearchLiveRegion message={liveMsg} />
          <DocsSearchModalBody
            canSearch={canSearch}
            documents={documents}
            goTo={goTo}
            indexLoading={indexLoading}
            loadError={loadError}
            query={query}
            recentDocs={recentDocs}
            results={results}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
/* eslint-enable complexity */

export function DocsSearch() {
  const ctx = useDocsSearchModal();

  return (
    <>
      <div className="mb-3 flex flex-col gap-2">
        <Button
          fullWidth
          className="justify-start text-default-600"
          data-testid="docs-search-trigger"
          size="sm"
          startContent={<Search className="h-4 w-4 shrink-0" />}
          variant="bordered"
          onPress={() => ctx.setOpen(true)}
        >
          <span className="truncate">Search documentation…</span>
          <kbd className="ml-auto hidden shrink-0 rounded border border-default-200 bg-default-100 px-1.5 py-0.5 font-mono text-[10px] text-default-500 sm:inline">
            ⌘K / Ctrl+K
          </kbd>
        </Button>
        <DocsSearchHint loadError={ctx.loadError} />
      </div>
      <DocsSearchDialog {...ctx} />
    </>
  );
}
